import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "servicesList",
    "upsellsList",
    "discountInput",
    "discountMessage",
    "discountRow",
    "discountAmount",
    "subtotal",
    "tax",
    "total",
    "totalAmount",
    "submitButton",
    "firstName",
    "firstNameError",
    "lastName",
    "lastNameError",
    "email",
    "emailError",
    "phone",
    "phoneError",
    "preferredDate",
    "preferredDateError"
  ]

  static values = {
    servicesPath: String,
    removeServiceUrl: String,
    removeUpsellUrl: String,
    slug: String,
    quoteId: String
  }

  connect() {
    this.updateSubmitButton()
    this.appliedDiscount = null
  }

  removeService(event) {
    event.preventDefault()
    const serviceId = event.currentTarget.dataset.serviceId
    const serviceRow = this.servicesListTarget.querySelector(`[data-service-id="${serviceId}"]`)

    if (serviceRow) {
      // Remove from DOM
      serviceRow.remove()

      // Update totals
      this.recalculateTotals()

      // Send request to update quote
      this.updateQuoteServices(serviceId)
    }
  }

  updateQuoteServices(removedServiceId) {
    // Get remaining service IDs
    const remainingServices = this.servicesListTarget.querySelectorAll('[data-service-id]')
    const serviceIds = Array.from(remainingServices).map(el => el.dataset.serviceId)

    // If no services left, redirect to services page
    if (serviceIds.length === 0) {
      window.location.href = this.servicesPathValue
      return
    }

    // Update quote via AJAX
    fetch(this.removeServiceUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ service_ids: serviceIds })
    })
  }

  removeUpsell(event) {
    event.preventDefault()
    const upsellId = event.currentTarget.dataset.upsellId
    const upsellRow = this.upsellsListTarget.querySelector(`[data-upsell-id="${upsellId}"]`)

    if (upsellRow) {
      // Remove from DOM
      upsellRow.remove()

      // Check if upsells section is now empty
      const remainingUpsells = this.upsellsListTarget.querySelectorAll('[data-upsell-id]')
      if (remainingUpsells.length === 0) {
        // Hide the entire upsells section
        const upsellsSection = this.upsellsListTarget.closest('[data-upsells-section]')
        if (upsellsSection) {
          upsellsSection.remove()
        }
      }

      // Update totals
      this.recalculateTotals()

      // Send request to update quote
      this.updateQuoteUpsells()
    }
  }

  updateQuoteUpsells() {
    // Get remaining upsell IDs
    const remainingUpsells = this.hasUpsellsListTarget
      ? this.upsellsListTarget.querySelectorAll('[data-upsell-id]')
      : []
    const upsellIds = Array.from(remainingUpsells).map(el => el.dataset.upsellId)

    // Update quote via AJAX
    fetch(this.removeUpsellUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ upsell_ids: upsellIds })
    })
  }

  recalculateTotals() {
    const serviceRows = this.servicesListTarget.querySelectorAll('[data-service-id]')
    let subtotal = 0

    serviceRows.forEach(row => {
      const price = parseFloat(row.dataset.price) || 0
      subtotal += price
    })

    // Add upsells to subtotal
    if (this.hasUpsellsListTarget) {
      const upsellRows = this.upsellsListTarget.querySelectorAll('[data-upsell-id]')
      upsellRows.forEach(row => {
        const price = parseFloat(row.dataset.price) || 0
        subtotal += price
      })
    }

    this.subtotalTarget.textContent = this.formatCurrency(subtotal)
    if (this.hasTotalTarget) this.totalTarget.textContent = this.formatCurrency(subtotal)
    if (this.hasTotalAmountTarget) this.totalAmountTarget.textContent = this.formatCurrency(subtotal)
  }

  async applyDiscount() {
    const code = this.discountInputTarget.value.trim()

    if (!code) {
      this.showDiscountMessage('Please enter a discount code', 'error')
      return
    }

    // Disable input while validating
    this.discountInputTarget.disabled = true

    try {
      const response = await fetch(`/api/widget/${this.slugValue}/validate_discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ code })
      })

      const data = await response.json()

      if (data.valid) {
        this.appliedDiscount = data
        this.showDiscountMessage(`${data.display_value} applied!`, 'success')
        this.updateTotalsWithDiscount()

        // Store discount code in hidden field for form submission
        this.addDiscountHiddenField(data.code)

        // Disable the input and show remove option
        this.discountInputTarget.readOnly = true
      } else {
        this.appliedDiscount = null
        this.showDiscountMessage(data.error || 'Invalid discount code', 'error')
      }
    } catch (error) {
      console.error('Error validating discount:', error)
      this.showDiscountMessage('Unable to validate code. Please try again.', 'error')
    } finally {
      this.discountInputTarget.disabled = false
    }
  }

  removeDiscount() {
    this.appliedDiscount = null
    this.discountInputTarget.value = ''
    this.discountInputTarget.readOnly = false
    this.removeDiscountHiddenField()
    this.hideDiscountMessage()
    this.recalculateTotals()
  }

  updateTotalsWithDiscount() {
    if (!this.appliedDiscount) {
      this.recalculateTotals()
      return
    }

    const serviceRows = this.servicesListTarget.querySelectorAll('[data-service-id]')
    let subtotal = 0

    serviceRows.forEach(row => {
      const price = parseFloat(row.dataset.price) || 0
      subtotal += price
    })

    // Add upsells to subtotal
    if (this.hasUpsellsListTarget) {
      const upsellRows = this.upsellsListTarget.querySelectorAll('[data-upsell-id]')
      upsellRows.forEach(row => {
        const price = parseFloat(row.dataset.price) || 0
        subtotal += price
      })
    }

    let discountAmount = 0
    if (this.appliedDiscount.type === 'percentage') {
      discountAmount = subtotal * (this.appliedDiscount.percentage / 100)
    } else if (this.appliedDiscount.type === 'fixed_amount') {
      discountAmount = this.appliedDiscount.amount_cents / 100
    }

    // Ensure discount doesn't exceed subtotal
    discountAmount = Math.min(discountAmount, subtotal)

    const total = subtotal - discountAmount

    // Update display
    this.subtotalTarget.textContent = this.formatCurrency(subtotal)

    // Show discount row
    if (this.hasDiscountRowTarget && discountAmount > 0) {
      this.discountRowTarget.classList.remove('hidden')
      if (this.hasDiscountAmountTarget) {
        this.discountAmountTarget.textContent = `-${this.formatCurrency(discountAmount)}`
      }
    }

    if (this.hasTotalTarget) this.totalTarget.textContent = this.formatCurrency(total)
    if (this.hasTotalAmountTarget) this.totalAmountTarget.textContent = this.formatCurrency(total)
  }

  addDiscountHiddenField(code) {
    // Remove existing if present
    this.removeDiscountHiddenField()

    const form = this.element.closest('form')
    if (form) {
      const hidden = document.createElement('input')
      hidden.type = 'hidden'
      hidden.name = 'discount_code'
      hidden.id = 'discount_code_hidden'
      hidden.value = code
      form.appendChild(hidden)
    }
  }

  removeDiscountHiddenField() {
    const existing = document.getElementById('discount_code_hidden')
    if (existing) {
      existing.remove()
    }
  }

  hideDiscountMessage() {
    if (this.hasDiscountMessageTarget) {
      this.discountMessageTarget.classList.add('hidden')
    }
    if (this.hasDiscountRowTarget) {
      this.discountRowTarget.classList.add('hidden')
    }
  }

  showDiscountMessage(message, type) {
    this.discountMessageTarget.textContent = message
    this.discountMessageTarget.classList.remove('hidden', 'text-green-600', 'text-red-600', 'text-gray-600')

    if (type === 'success') {
      this.discountMessageTarget.classList.add('text-green-600')
    } else if (type === 'error') {
      this.discountMessageTarget.classList.add('text-red-600')
    } else {
      this.discountMessageTarget.classList.add('text-gray-600')
    }
  }

  updateSubmitButton() {
    // Button is always enabled — terms acceptance is implicit on payment confirmation
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }

    const visibleBtn = document.getElementById('visible-checkout-submit-btn')
    if (visibleBtn) {
      visibleBtn.disabled = false
    }
  }

  formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  // Validation methods
  validateField(event) {
    const field = event.target
    const fieldName = this.getFieldName(field)

    if (fieldName) {
      this.validateSingleField(fieldName)
    }
  }

  clearFieldError(event) {
    const field = event.target
    const fieldName = this.getFieldName(field)

    if (fieldName) {
      this.clearError(fieldName)
    }
  }

  getFieldName(field) {
    // Map target to field name - use hasXxxTarget checks to avoid errors
    if (this.hasFirstNameTarget && field === this.firstNameTarget) return 'firstName'
    if (this.hasLastNameTarget && field === this.lastNameTarget) return 'lastName'
    if (this.hasEmailTarget && field === this.emailTarget) return 'email'
    if (this.hasPhoneTarget && field === this.phoneTarget) return 'phone'
    if (this.hasPreferredDateTarget && field === this.preferredDateTarget) return 'preferredDate'
    return null
  }

  validateSingleField(fieldName) {
    const validators = {
      firstName: () => this.hasFirstNameTarget ? this.validateRequired(this.firstNameTarget, this.firstNameErrorTarget, 'First name is required') : true,
      lastName: () => this.hasLastNameTarget ? this.validateRequired(this.lastNameTarget, this.lastNameErrorTarget, 'Last name is required') : true,
      email: () => this.validateEmail(),
      phone: () => this.validatePhone(),
      preferredDate: () => this.hasPreferredDateTarget ? this.validateRequired(this.preferredDateTarget, this.preferredDateErrorTarget, 'Please select when you need service') : true
    }

    if (validators[fieldName]) {
      return validators[fieldName]()
    }
    return true
  }

  validateRequired(field, errorTarget, message) {
    const value = field.value.trim()

    if (!value) {
      this.showError(field, errorTarget, message)
      return false
    }

    this.clearErrorForField(field, errorTarget)
    return true
  }

  validateEmail() {
    if (!this.hasEmailTarget) return true

    const email = this.emailTarget.value.trim()

    if (!email) {
      this.showError(this.emailTarget, this.emailErrorTarget, 'Email address is required')
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      this.showError(this.emailTarget, this.emailErrorTarget, 'Please enter a valid email address')
      return false
    }

    // Check for common typos
    const commonTypos = {
      'gmail.con': 'gmail.com',
      'gmial.com': 'gmail.com',
      'gmal.com': 'gmail.com',
      'yahoo.con': 'yahoo.com',
      'hotmail.con': 'hotmail.com',
      'outlok.com': 'outlook.com'
    }

    const domain = email.split('@')[1]?.toLowerCase()
    if (domain && commonTypos[domain]) {
      this.showError(this.emailTarget, this.emailErrorTarget, `Did you mean ${email.split('@')[0]}@${commonTypos[domain]}?`)
      return false
    }

    this.clearErrorForField(this.emailTarget, this.emailErrorTarget)
    return true
  }

  validatePhone() {
    if (!this.hasPhoneTarget) return true

    const phone = this.phoneTarget.value.trim()

    if (!phone) {
      this.showError(this.phoneTarget, this.phoneErrorTarget, 'Phone number is required')
      return false
    }

    // Remove all non-digits
    const digits = phone.replace(/\D/g, '')

    if (digits.length < 10) {
      this.showError(this.phoneTarget, this.phoneErrorTarget, 'Please enter a valid 10-digit phone number')
      return false
    }

    this.clearErrorForField(this.phoneTarget, this.phoneErrorTarget)
    return true
  }

  formatPhone(event) {
    const input = event.target
    let value = input.value.replace(/\D/g, '')

    if (value.length > 0) {
      if (value.length <= 3) {
        value = `(${value}`
      } else if (value.length <= 6) {
        value = `(${value.slice(0, 3)}) ${value.slice(3)}`
      } else {
        value = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`
      }
    }

    input.value = value
  }

  showError(field, errorTarget, message) {
    field.classList.add('form-input-error')
    if (errorTarget) {
      errorTarget.textContent = message
      errorTarget.classList.remove('hidden')
    }
  }

  clearError(fieldName) {
    // Use hasXxxTarget checks to avoid accessing undefined targets
    const fieldMap = {
      firstName: this.hasFirstNameTarget ? [this.firstNameTarget, this.firstNameErrorTarget] : null,
      lastName: this.hasLastNameTarget ? [this.lastNameTarget, this.lastNameErrorTarget] : null,
      email: this.hasEmailTarget ? [this.emailTarget, this.emailErrorTarget] : null,
      phone: this.hasPhoneTarget ? [this.phoneTarget, this.phoneErrorTarget] : null,
      preferredDate: this.hasPreferredDateTarget ? [this.preferredDateTarget, this.preferredDateErrorTarget] : null
    }

    const mapping = fieldMap[fieldName]
    if (mapping) {
      const [field, errorTarget] = mapping
      if (field && errorTarget) {
        this.clearErrorForField(field, errorTarget)
      }
    }
  }

  clearErrorForField(field, errorTarget) {
    field.classList.remove('form-input-error')
    if (errorTarget) {
      errorTarget.textContent = ''
      errorTarget.classList.add('hidden')
    }
  }

  validateAllFields() {
    let isValid = true

    if (this.hasFirstNameTarget) {
      isValid = this.validateSingleField('firstName') && isValid
    }
    if (this.hasLastNameTarget) {
      isValid = this.validateSingleField('lastName') && isValid
    }
    if (this.hasEmailTarget) {
      isValid = this.validateSingleField('email') && isValid
    }
    if (this.hasPhoneTarget) {
      isValid = this.validateSingleField('phone') && isValid
    }
    if (this.hasPreferredDateTarget) {
      isValid = this.validateSingleField('preferredDate') && isValid
    }

    return isValid
  }
}
